import { useEffect, useRef, useState, type ReactNode } from "react";
import { onAuthStateChanged, signOut as fbSignOut, type User } from "firebase/auth";
import { auth } from "../firebase";
import { authedFetch } from "../lib/api";
import { closePlaybackSession } from "../media";
import { localAuthBypassEnabled } from "../local-development";
import { AuthContext, type AuthState, type UserProfile } from "./auth-context";

// Firebase Auth alone is not access control — any Google account can sign in.
// After onAuthStateChanged fires we run a backend probe; only a 200 flips
// the UI to "allowed".

export function AuthProvider({ children }: { children: ReactNode }) {
  const localBypass = localAuthBypassEnabled();
  const [state, setState] = useState<AuthState>(() => localBypass
    ? {
        status: "allowed",
        user: { uid: "local-dev" } as User,
        profile: { email: "dev@local", user_id: "local-dev", role: "admin" },
      }
    : { status: "idle" });

  // Sticky-denied: once denied, ignore the user=null fbSignOut triggers so
  // the UI keeps showing the denied banner instead of flipping back to idle.
  const deniedRef = useRef(false);
  // Verified-uid: hourly token refresh re-fires onAuthStateChanged with the
  // same user; skip re-probing when we've already verified this uid.
  const verifiedUidRef = useRef<string | null>(null);

  useEffect(() => {
    if (localBypass) return;

    let active = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        verifiedUidRef.current = null;
        if (deniedRef.current) return;
        setState({ status: "idle" });
        return;
      }

      if (verifiedUidRef.current === user.uid) return;

      setState({ status: "verifying", user });

      try {
        const res = await authedFetch("/api/me");
        if (!active) return;

        if (res.status === 401 || res.status === 403) {
          deniedRef.current = true;
          await fbSignOut(auth);
          if (!active) return;
          setState({
            status: "denied",
            reason: res.status === 403 ? "Not on allowlist" : "Token rejected",
          });
          return;
        }
        if (!res.ok) throw new Error(`probe failed: ${res.status}`);

        const profile = (await res.json()) as UserProfile;
        if (profile.role !== "admin" && profile.role !== "user") {
          throw new Error("probe returned an invalid role");
        }

        verifiedUidRef.current = user.uid;
        setState({ status: "allowed", user, profile });
      } catch (err) {
        if (!active) return;
        deniedRef.current = true;
        await fbSignOut(auth);
        if (!active) return;
        setState({ status: "denied", reason: (err as Error).message });
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [localBypass]);

  const signOut = async () => {
    if (localBypass) return;
    deniedRef.current = false;
    verifiedUidRef.current = null;
    await closePlaybackSession().catch(() => undefined);
    await fbSignOut(auth);
    setState({ status: "idle" });
  };

  return <AuthContext.Provider value={{ state, signOut }}>{children}</AuthContext.Provider>;
}
